import clsx from 'clsx';
import React, {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { borderFor, controlBase } from './Field';
import { useEscape } from './Layers';
import { IconCheck, IconChevronDown } from './icons';

/**
 * TimeInput: a time of day, typed or picked. The value is "HH:MM" (24h);
 * what people see and type is 12-hour ("9:30 AM").
 *
 * - Type it the way you'd say it: `11am`, `10:30p`, `1730`, `noon`. With no
 *   AM/PM, the time keeps the half of the day it already had (or the half
 *   of `suggest`), so `10` on a 10 PM close stays PM.
 * - The list under it is every quarter hour, drawn by us (no OS popup). It
 *   follows the typing: the time the text resolves to is highlighted, so an
 *   ambiguous entry is visible before it is committed.
 * - Enter or leaving the field commits; text that isn't a time puts the old
 *   value back. ↓ ↑ open and step through the list; Esc closes and reverts
 *   (and is consumed before an enclosing Dialog sees it).
 * - WAI-ARIA editable combobox with list autocomplete; focus stays in the
 *   input throughout.
 */
export interface TimeInputProps {
  /** "HH:MM", or '' when not set. */
  value: string;
  onChange: (value: string) => void;
  /** Where the list opens, and which half of the day bare hours mean, while empty. */
  suggest?: string;
  placeholder?: string;
  invalid?: boolean;
  disabled?: boolean;
  size?: 'sm' | 'md';
  id?: string;
  className?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
}

const LIST_MAX_HEIGHT = 240;

/** "21:30" → "9:30 PM". */
export function formatTime(value: string): string {
  if (!value) return '';
  const [h, m] = value.split(':').map((n) => parseInt(n, 10));
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

/**
 * What someone typed → "HH:MM", or null when it isn't a time. Bare hours
 * 1–11 take `half`; a bare 12 is noon.
 */
export function parseTime(
  text: string,
  half: 'am' | 'pm' = 'am',
): string | null {
  const t = text.toLowerCase().replace(/[\s.]/g, '');
  if (t === 'noon') return '12:00';
  if (t === 'midnight') return '00:00';
  const match = t.match(/^(\d{1,2})(?::?(\d{2}))?(a|p|am|pm)?$/);
  if (!match) return null;
  let h = Number(match[1]);
  const m = match[2] ? Number(match[2]) : 0;
  const suffix = match[3]?.[0];
  if (m > 59) return null;
  if (suffix) {
    if (h < 1 || h > 12) return null;
    h = (h % 12) + (suffix === 'p' ? 12 : 0);
  } else if (h > 23) {
    return null;
  } else if (h >= 1 && h <= 11 && half === 'pm') {
    h += 12;
  }
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

const QUARTER_HOURS = Array.from(
  { length: 96 },
  (_, i) =>
    `${String(Math.floor(i / 4)).padStart(2, '0')}:${String((i % 4) * 15).padStart(2, '0')}`,
);

const halfOf = (value: string | undefined): 'am' | 'pm' =>
  value && parseInt(value, 10) >= 12 ? 'pm' : 'am';

export const TimeInput: React.FC<TimeInputProps> = ({
  value,
  onChange,
  suggest,
  placeholder = 'Add time',
  invalid,
  disabled,
  size = 'md',
  id,
  className,
  'aria-label': ariaLabel,
  'aria-describedby': describedBy,
}) => {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  // What's being typed; null while the field just shows the value.
  const [query, setQuery] = useState<string | null>(null);
  const [active, setActive] = useState(-1);
  const [above, setAbove] = useState(false);
  // Centre the list on open; after that, only nudge it.
  const centre = useRef(false);

  // A time off the quarter hour (from a POS or Google) stays in the list.
  const options =
    value && !QUARTER_HOURS.includes(value)
      ? [...QUARTER_HOURS, value].sort()
      : QUARTER_HOURS;
  const half = halfOf(value || suggest);
  const nearest = (time: string) => {
    const i = options.findIndex((o) => o >= time);
    return i === -1 ? options.length - 1 : i;
  };
  const parsed = query === null ? null : parseTime(query, half);
  const unreadable = query !== null && query.trim() !== '' && parsed === null;

  const show = () => {
    if (open || disabled) return;
    const target = value || suggest;
    setActive(target ? nearest(target) : -1);
    centre.current = true;
    setOpen(true);
  };
  const close = () => {
    setOpen(false);
    setActive(-1);
  };
  const commit = (next: string | null) => {
    if (next && next !== value) onChange(next);
    setQuery(null);
    close();
  };
  const revert = () => {
    setQuery(null);
    close();
  };

  useEscape(open, revert);

  // Flip above the field when there isn't room below it.
  useLayoutEffect(() => {
    if (!open || !rootRef.current) return;
    const r = rootRef.current.getBoundingClientRect();
    const below = window.innerHeight - r.bottom;
    setAbove(below < LIST_MAX_HEIGHT + 8 && r.top > below);
  }, [open]);

  useEffect(() => {
    const list = listRef.current;
    if (!open || !list || active < 0) return;
    const el = list.querySelector<HTMLElement>(`[data-index="${active}"]`);
    if (!el) return;
    if (centre.current) {
      centre.current = false;
      list.scrollTop =
        el.offsetTop - list.clientHeight / 2 + el.offsetHeight / 2;
    } else {
      el.scrollIntoView?.({ block: 'nearest' });
    }
  }, [open, active]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) {
        show();
        return;
      }
      const step = e.key === 'ArrowDown' ? 1 : -1;
      const next =
        active < 0
          ? step === 1
            ? 0
            : options.length - 1
          : Math.min(options.length - 1, Math.max(0, active + step));
      setActive(next);
      // Stepping writes the time into the field, so Enter commits it.
      setQuery(formatTime(options[next]));
    } else if (e.key === 'Enter') {
      if (!open && query === null) return;
      e.preventDefault();
      if (unreadable) revert();
      else commit(parsed ?? (active >= 0 ? options[active] : null));
    }
  };

  return (
    <div ref={rootRef} className={clsx('relative', className)}>
      <input
        id={id}
        type="text"
        role="combobox"
        aria-label={ariaLabel}
        aria-describedby={describedBy}
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-autocomplete="list"
        aria-activedescendant={
          open && active >= 0 ? `${listId}-${active}` : undefined
        }
        aria-invalid={invalid || undefined}
        autoComplete="off"
        spellCheck={false}
        inputMode="text"
        disabled={disabled}
        placeholder={placeholder}
        value={query ?? formatTime(value)}
        onFocus={(e) => e.currentTarget.select()}
        onClick={show}
        onChange={(e) => {
          const text = e.target.value;
          setQuery(text);
          if (!open) {
            centre.current = true;
            setOpen(true);
          }
          const time = parseTime(text, half);
          if (time) setActive(nearest(time));
        }}
        onKeyDown={onKeyDown}
        onBlur={() => {
          if (query === null) close();
          else if (unreadable || query.trim() === '') revert();
          else commit(parsed);
        }}
        className={clsx(
          controlBase,
          borderFor(invalid || unreadable),
          size === 'sm' ? 'h-7 text-v2-12' : 'h-8',
          'v2-num pl-2.5 pr-7',
        )}
      />
      <IconChevronDown
        className={clsx(
          'pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-v2-fg-3 transition-transform duration-100',
          open && 'rotate-180',
        )}
      />
      {open && (
        <div
          // Keeps focus in the input so picking a time doesn't blur it first.
          onMouseDown={(e) => e.preventDefault()}
          className={clsx(
            'v2-fade-in absolute left-0 z-50 w-full min-w-[132px] overflow-hidden rounded-v2-lg border border-v2-line bg-v2-surface shadow-v2-float',
            above ? 'bottom-full mb-1' : 'top-full mt-1',
          )}
        >
          {unreadable && (
            <p className="border-b border-v2-line px-2.5 py-2 text-v2-12 leading-4 text-v2-fg-3">
              Try a time like 9am or 5:30pm.
            </p>
          )}
          <ul
            ref={listRef}
            id={listId}
            role="listbox"
            aria-label={ariaLabel}
            style={{ maxHeight: LIST_MAX_HEIGHT }}
            className="overflow-y-auto p-1"
          >
            {options.map((option, i) => {
              const selected = option === value;
              return (
                <li
                  key={option}
                  id={`${listId}-${i}`}
                  data-index={i}
                  role="option"
                  aria-selected={selected}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => commit(option)}
                  className={clsx(
                    'v2-num flex cursor-pointer items-center justify-between gap-2 rounded-v2 px-2 py-1.5 text-v2-13 transition-colors duration-75',
                    i === active
                      ? 'bg-v2-sunken text-v2-fg'
                      : selected
                        ? 'text-v2-fg'
                        : 'text-v2-fg-2',
                    selected && 'font-medium',
                  )}
                >
                  {formatTime(option)}
                  {selected && (
                    <IconCheck size={14} className="shrink-0 text-v2-accent" />
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};
